import * as React from 'react';
import debounce from 'lodash/debounce';
import { useQuery } from '@tanstack/react-query';
import { AutoComplete, type AutoCompleteProps } from 'antd';
import * as Store from '../store';
import * as Styled from './styled';

export interface CustomTagSelectorProps extends AutoCompleteProps {
  defaultValue?: string;
}

const CustomTagSelector = (props: CustomTagSelectorProps) => {
  const { defaultValue, ...params } = props;
  const [search, setSearch] = React.useState(defaultValue);
  const onCustomTagSearch = Store.useOnCustomTagSearch();
  const searchedTags = useQuery(
    ['filter-dialog', 'filters', 'tags', 'key', search],
    async () => {
      try {
        return (await onCustomTagSearch?.(search)) ?? [];
      } catch {
        return [{ text: search, value: search }];
      }
    },
    {
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      enabled: (search?.length ?? 0) > 2,
    },
  );

  const options = React.useMemo(() => {
    const res =
      searchedTags.data?.map((it) => ({
        label: it.text,
        value: it.value,
      })) ?? [];

    if (!res.find((it) => it.label === search) && search) {
      res.unshift({ label: search, value: search });
    }

    return res;
  }, [searchedTags.data, search]);

  const handleSearch = React.useMemo(() => debounce((v: string) => setSearch(v.trim()), 500), []);

  return (
    <AutoComplete
      {...params}
      autoFocus
      placeholder={defaultValue || 'Search custom tag'}
      options={options}
      // Custom tag options are always non-empty
      notFoundContent={searchedTags.isLoading ? <Styled.DropdownSpin /> : null}
      popupRender={(menu) => (searchedTags.isLoading ? <Styled.DropdownSpin /> : menu)}
      onSearch={handleSearch}
    />
  );
};

export default React.memo(CustomTagSelector);
